import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowRight, Users, Map, Trophy, ChevronRight, Star, Zap } from "lucide-react";
import { getCityBySlug, SPORT_DISPLAY } from "@/data/cities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORT_EMOJI = {
  tennis: "🎾", pickleball: "🏓", golf: "⛳", padel: "🏸",
  squash: "🟡", badminton: "🏸", climbing: "🧗", ski: "⛷️",
  "table-tennis": "🏓",
};

export default function CityPage() {
  const { stateSlug, citySlug } = useParams();
  const city = getCityBySlug(stateSlug, citySlug);
  const [venues, setVenues] = useState([]);
  // Per-city counts from /api/cities. Null until loaded, so we can tell
  // "not here yet" apart from "still loading" and never render a guess.
  const [cityStats, setCityStats] = useState(null);

  useEffect(() => {
    if (!city) return;
    fetch(`${API}/venues?city=${encodeURIComponent(city.name)}`)
      .then((r) => r.json())
      .then((data) => setVenues(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [city]);

  useEffect(() => {
    if (!city) return;
    fetch(`${API}/cities`)
      .then((r) => r.json())
      .then((data) => {
        const match = (data.cities || []).find(
          (c) => c.city.toLowerCase() === city.name.toLowerCase()
        );
        setCityStats(match || { venues: 0, players: 0, sports: [] });
      })
      .catch(() => setCityStats({ venues: 0, players: 0, sports: [] }));
  }, [city]);

  if (!city) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🗺️</div>
          <h1 className="text-3xl font-heading font-black text-brand-navy mb-3">City Not Found</h1>
          <p className="text-gray-500 mb-6">We haven't mapped this city yet — but we're growing fast.</p>
          <Link
            to="/find-partners"
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange-hover transition-colors"
          >
            <Users size={16} /> Browse All Players
          </Link>
        </div>
      </div>
    );
  }

  // Count real venues per sport from the venues we actually fetched. This
  // replaces city.venueCounts, a static dataset that claimed e.g. 30 tennis
  // venues in a city with none. A sport with no mapped venue is simply absent.
  const venuesBySport = {};
  for (const v of venues) {
    for (const s of v.sports || []) {
      venuesBySport[s] = (venuesBySport[s] || 0) + 1;
    }
  }
  const sportEntries = Object.entries(venuesBySport).sort((a, b) => b[1] - a[1]);
  const venueCount = venues.length;
  const playerCount = cityStats?.players ?? 0;
  // No mapped venues means we have no presence here, and the page says so
  // rather than showing a number nobody can verify.
  const isLaunched = venueCount > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero — dark brand-navy for proper white text contrast */}
      <div className="bg-brand-navy pt-24 pb-14 px-4 relative overflow-hidden dark-section">
        {/* Decorative arc */}
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full border-[28px] border-brand-green/20 pointer-events-none" />
        <div className="absolute -right-6 top-1/2 -translate-y-1/2 w-36 h-36 rounded-full border-[18px] border-brand-orange/20 pointer-events-none" />

        <div className="max-w-5xl mx-auto relative">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 mb-5 text-sm text-white/50">
            <MapPin size={13} className="text-brand-orange" aria-hidden="true" />
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={12} aria-hidden="true" />
            <Link to="/find-partners" className="hover:text-white transition-colors">Cities</Link>
            <ChevronRight size={12} aria-hidden="true" />
            <span className="text-white/80" aria-current="page">{city.name}, {city.stateCode}</span>
          </nav>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-white mb-4 leading-tight">
            Find Sports Partners<br />
            in <span className="text-brand-orange">{city.name}</span>
          </h1>
          <p className="text-white/70 max-w-xl mb-8 leading-relaxed text-lg">
            {city.description}
          </p>

          {/* Sport pills */}
          <div className="flex flex-wrap gap-2 mb-8" role="list" aria-label="Popular sports in this city">
            {city.topSports.map((sport) => (
              <Link
                key={sport}
                role="listitem"
                to={`/find-partners?sport=${sport}&city=${encodeURIComponent(city.name)}`}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-white/10 text-white border border-white/20 hover:bg-brand-orange hover:border-brand-orange transition-all"
              >
                {SPORT_EMOJI[sport]} {SPORT_DISPLAY[sport] || sport}
              </Link>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to={`/find-partners?city=${encodeURIComponent(city.name)}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange-hover transition-colors shadow-lg"
              data-testid="city-find-partners-btn"
            >
              <Users size={18} aria-hidden="true" /> Find Partners in {city.name}
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white/20 text-white rounded-xl hover:border-brand-green hover:text-brand-green transition-colors"
            >
              Join Free <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-white border-b border-gray-200 py-6 px-4 shadow-sm">
        <div className="max-w-5xl mx-auto">
          {!isLaunched && (
            <p
              className="text-sm text-gray-600 bg-brand-orange/5 border border-brand-orange/20 rounded-xl px-4 py-3 mb-5"
              data-testid="city-not-launched"
            >
              We haven't launched in {city.name} yet — no venues are mapped, so there
              are no players to match here. <strong>Fort Worth, TX</strong> is our
              launch city;{" "}
              <Link to="/us/texas/fort-worth" className="text-brand-orange font-semibold hover:underline">
                see what's live there
              </Link>
              .
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { value: playerCount, label: "Active Players", color: "text-brand-orange" },
              { value: venueCount, label: "Venues", color: "text-brand-green" },
              { value: sportEntries.length, label: "Sports", color: "text-brand-navy" },
              { value: "Free", label: "To Join", color: "text-brand-orange" },
            ].map(({ value, label, color }) => (
              <div key={label} className="text-center">
                <div className={`text-2xl font-heading font-black ${color}`}>{value}</div>
                <div className="text-xs text-gray-500 mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-12">

        {/* City highlights — only where we actually have venues listed */}
        {isLaunched && city.highlights?.length > 0 && (
          <section aria-labelledby="highlights-heading">
            <div className="flex items-center gap-2 mb-4">
              <Star size={18} className="text-brand-orange" aria-hidden="true" />
              <h2 id="highlights-heading" className="text-xl font-heading font-bold text-brand-navy">Highlights</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {city.highlights.map((h) => (
                <div key={h} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-brand-orange/30 hover:-translate-y-0.5 transition-all shadow-sm">
                  <div className="flex items-start gap-2">
                    <Zap size={14} className="text-brand-green mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <p className="text-sm text-brand-navy font-medium">{h}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Top Venues */}
        {venues.length > 0 && (
          <section aria-labelledby="venues-heading">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Map size={18} className="text-brand-orange" aria-hidden="true" />
                <h2 id="venues-heading" className="text-xl font-heading font-bold text-brand-navy">
                  Top Venues in {city.name}
                </h2>
              </div>
              <Link to="/venues" className="text-sm text-brand-green hover:underline flex items-center gap-1">
                See all <ArrowRight size={12} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {venues.slice(0, 6).map((v) => (
                <div
                  key={v.id}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-brand-orange/40 hover:-translate-y-0.5 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-bold text-brand-navy text-sm">{v.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                      v.venue_type === "public"
                        ? "bg-brand-green/10 text-brand-green"
                        : "bg-brand-navy/10 text-brand-navy"
                    }`}>
                      {v.venue_type}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {v.sports?.slice(0, 3).map((s) => (
                      <span key={s} className="text-xs px-2 py-0.5 bg-brand-orange/10 text-brand-orange rounded-full font-medium capitalize">
                        {SPORT_EMOJI[s]} {SPORT_DISPLAY[s] || s}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className={v.indoor ? "text-brand-navy font-medium" : "text-gray-400"}>
                      {v.indoor ? "Indoor" : "Outdoor"}
                    </span>
                    {v.fee && <span>· {v.fee}</span>}
                    {v.courts && <span>· {v.courts} courts</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Sports scene — derived from the venues we actually fetched */}
        {isLaunched && (
        <section aria-labelledby="sports-heading">
          <div className="flex items-center gap-2 mb-5">
            <Trophy size={18} className="text-brand-orange" aria-hidden="true" />
            <h2 id="sports-heading" className="text-xl font-heading font-bold text-brand-navy">
              Sports Scene in {city.name}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {sportEntries.map(([sport, count]) => (
              <Link
                key={sport}
                to={`/find-partners?sport=${sport}&city=${encodeURIComponent(city.name)}`}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-brand-orange/40 hover:bg-brand-orange/5 hover:-translate-y-0.5 transition-all group shadow-sm"
                data-testid={`city-sport-${sport}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl" aria-hidden="true">{SPORT_EMOJI[sport] || "🏅"}</span>
                  <div>
                    <span className="font-semibold text-brand-navy group-hover:text-brand-orange transition-colors">
                      {SPORT_DISPLAY[sport] || sport}
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5">{count} venue{count > 1 ? "s" : ""} in {city.name}</p>
                  </div>
                </div>
                <ArrowRight size={14} className="text-gray-400 group-hover:text-brand-orange transition-colors" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
        )}

        {/* CTA */}
        <section className="bg-brand-navy rounded-2xl p-8 text-center relative overflow-hidden dark-section">
          <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full border-[16px] border-brand-green/20 pointer-events-none" />
          <h2 className="text-2xl font-heading font-bold text-white mb-2">
            Ready to play in {city.name}?
          </h2>
          <p className="text-white/70 mb-6 text-sm">
            {isLaunched
              ? playerCount > 0
                ? `Join ${playerCount} ${playerCount === 1 ? "player" : "players"} already in ${city.name} — it's free.`
                : `Be the first player in ${city.name} — it's free.`
              : `We're not in ${city.name} yet. Fort Worth is our launch city.`}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-3 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange-hover transition-colors shadow-lg"
          >
            Get Started Free <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </section>

      </div>
    </div>
  );
}
